import { CHANNEL_CHECK_UNAVAILABLE_MESSAGE, type GateChannel } from "@bing/shared";
import { useEffect, useRef, useState, type ReactNode } from "react";
import { ApiError } from "../api/client";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { fa } from "../i18n/fa";
import { ChannelGate } from "../screens/ChannelGate";
import { useSession, useSessionStore } from "../store/context";
import { parseChannelRefusal } from "./channel-refusal";

/**
 * A TABLE JOIN THE GAME-SERVER REFUSED FOR CHANNEL MEMBERSHIP — THE GATE, NEVER THE DEAD END
 * (gate-table-refusal, 2026-10-09).
 *
 * The api and the game-server keep separate membership caches, so `/me` can pass while the table's
 * `assertChannelMember` refuses. The refusal used to render as an ordinary join failure: the gate
 * sentence over no channels, no button. Now the container that owns the join (an online table, or a
 * solo game's new-game join) hands its store's status and error here, and gets back the real
 * channel-join screen ({@link ChannelGate}) while the refusal stands — or `null`.
 *
 * WHICH LIST (card-db's contract, 2026-10-09): `/me` is asked FIRST, whichever form arrived.
 *  • 403 `CHANNELS_REQUIRED` → the api's list (its `joinUrl`s carry the campaign's credit);
 *  • 200 → the refusal's own `<title>\t<url>` lines (the stale-cache case);
 *  • any other failure → the refusal's lines.
 * No usable line → the sentence and «عضو شدم» alone, which is still a way forward.
 *
 * «عضو شدم» RETRIES THE JOIN (`restart`: a fresh store for the same table/flow), not `/me`. The gate
 * stays mounted while the retry connects, so a refusal again shows `GATE_STILL_MISSING_MESSAGE`
 * (the gate's own notice); anything else takes the gate down and the container shows what it is.
 *
 * ⚠️ It never covers a live table: it acts ONLY on the container's own `error` status carrying the
 * channel refusal, which no seated, playing store ever reaches.
 */
/**
 * How long the gate's `/me` may take before the refusal's own channel lines are shown instead (card-a5
 * item 5: never a spinner without end). `/me` only picks WHICH list to draw — the api's (its campaign
 * links) or the wire's — so a slow answer must not hold the «در حال بررسی…» screen: the api answers
 * within its own 3 s limit when healthy, and the boot's measured p99 for a whole cold handshake is 22 s.
 */
export const TABLE_GATE_ME_TIMEOUT_MS = 10_000;

/** Whether a join failure is the game-server's «Telegram did not answer in time» refusal (exact). */
export function isChannelCheckUnavailable(error: string | null): boolean {
  return error?.trim() === CHANNEL_CHECK_UNAVAILABLE_MESSAGE;
}

export function useTableChannelGate({
  status,
  error,
  restart,
  onLeave,
}: {
  /** The container store's status/phase («connecting», «error», …). */
  status: string;
  /** The container store's error (the server's refusal, verbatim when Persian). */
  error: string | null;
  /** Re-attempt the same join — a fresh store for the same table or flow. */
  restart: () => void;
  /** The gate's way out, back to the lobby. */
  onLeave: () => void;
}): ReactNode {
  const api = useSession((s) => s.api);
  const session = useSessionStore();
  /** The refusal on screen: the channels to list (null while `/me` is being asked). */
  const [refusal, setRefusal] = useState<{ channels: GateChannel[] | null } | null>(null);
  const settle = useRef<(() => void) | null>(null);
  /** Whether a refusal is on screen (read by the effect, which must not depend on the state). */
  const gated = useRef(false);

  useEffect(() => {
    if (status === "connecting") return;
    const parsed = status === "error" ? parseChannelRefusal(error) : null;
    settle.current?.();
    settle.current = null;
    if (parsed === null) {
      // Not (or no longer) refused — the gate goes. A list the api put in the session mirror for this
      // refusal is refreshed, so the lobby does not greet a member with a stale gate.
      if (gated.current && session.getState().channelGate !== null) {
        void api.getMe().catch(() => undefined);
      }
      gated.current = false;
      setRefusal(null);
      return;
    }
    gated.current = true;
    setRefusal((r) => ({ channels: r?.channels ?? null }));
    // BOUNDED (card-a5 item 5): the abort cancels a fetch that honours it, and the race ends the wait
    // even where one does not — a `/me` that never answers falls to the refusal's own lines.
    const abort = new AbortController();
    let timer: number | undefined;
    const limit = new Promise<never>((_, reject) => {
      timer = window.setTimeout(() => {
        abort.abort();
        reject(new Error("timeout"));
      }, TABLE_GATE_ME_TIMEOUT_MS);
    });
    void Promise.race([api.getMe({ signal: abort.signal }), limit])
      .finally(() => window.clearTimeout(timer))
      .then(
        () => parsed,
        (e: unknown) => {
          if (e instanceof ApiError && e.code === "CHANNELS_REQUIRED") {
            const list = session.getState().channelGate;
            // The mirror holds a non-empty list or null (`setChannelGate` folds [] to null).
            if (list) return list;
          }
          return parsed;
        },
      )
      // An answer that lands after the refusal cleared (the retry got in) changes nothing.
      .then((channels) => setRefusal((r) => (r ? { channels } : r)));
  }, [status, error, api, session]);

  // TELEGRAM DID NOT ANSWER IN TIME (card-a5 item 5): the game-server could not check membership and
  // nothing vouches for this player. Not a «join these» — nothing said they are not a member — but the
  // sentence and a RETRY of the same join, with the way back beside it.
  if (status === "error" && isChannelCheckUnavailable(error)) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p
            role="status"
            style={{ color: "var(--danger)", margin: 0, fontWeight: 700, lineHeight: 1.7 }}
          >
            {CHANNEL_CHECK_UNAVAILABLE_MESSAGE}
          </p>
          <Button variant="primary" onClick={restart}>
            {fa.channelGate.retry}
          </Button>
          <Button onClick={onLeave}>{fa.channelGate.back}</Button>
        </div>
      </div>
    );
  }
  if (!refusal) return null;
  if (refusal.channels === null)
    return <Boot caption={fa.channelGate.checking} onLeave={onLeave} />;
  return (
    <ChannelGate
      channels={refusal.channels}
      onRecheck={() =>
        new Promise<void>((resolve) => {
          settle.current = resolve;
          restart();
        })
      }
      onLeave={onLeave}
    />
  );
}
